import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { GiFairyWings } from "react-icons/gi";
import { FaMapMarkerAlt, FaRegCalendarAlt } from "react-icons/fa";
import "./Event.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

const getYearFromDate = (dateString) => new Date(dateString).getFullYear().toString();
const getMonthString = (dateString) => new Date(dateString).toLocaleString('en-US', { month: 'short' }).toUpperCase();
const getDayString = (dateString) => new Date(dateString).getDate().toString().padStart(2, '0');

const formatTimeAmPm = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':');
    const hours = parseInt(h, 10);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hr12 = hours % 12 || 12;
    return `${hr12.toString().padStart(2, '0')}:${m} ${suffix}`;
};

const formatEventDateTime = (start, end, sTime, eTime) => {
    if (!start) return '';
    
    const startDateStr = new Date(start).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    const isSameDay = !end || (start.split('T')[0] === end.split('T')[0]);
    
    const startTimeStr = sTime ? formatTimeAmPm(sTime) : '';
    const endTimeStr = eTime ? formatTimeAmPm(eTime) : '';

    if (isSameDay) {
        let timeStr = '';
        if (startTimeStr && endTimeStr) {
            timeStr = ` (Start Time: ${startTimeStr} - End Time: ${endTimeStr})`;
        } else if (startTimeStr) {
            timeStr = ` (Start Time: ${startTimeStr})`;
        } else if (endTimeStr) {
            timeStr = ` (End Time: ${endTimeStr})`;
        }
        return `${startDateStr}${timeStr}`;
    } else {
        const endDateStr = new Date(end).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
        const startPart = startTimeStr ? `${startDateStr}, ${startTimeStr}` : startDateStr;
        const endPart = endTimeStr ? `${endDateStr}, ${endTimeStr}` : endDateStr;
        return `${startPart} to ${endPart}`;
    }
};

function Event() {
  const [eventsData, setEventsData] = useState([]);
  const [activeYear, setActiveYear] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/events/public`);
        const data = await response.json();
        setEventsData(data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const years = ["All", ...new Set(eventsData.map((item) => getYearFromDate(item.eventDate)))].sort().reverse();

  const filteredEvents = activeYear === "All"
    ? eventsData
    : eventsData.filter((item) => getYearFromDate(item.eventDate) === activeYear);

  // Generate structured schema logic for Event list
  const structuredSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Rajput Shiksha Kosh Events",
    "description": "List of upcoming and past events of Rajput Shiksha Kosh.",
    "itemListElement": filteredEvents.map((event, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "url": `${window.location.origin}/events/${event.slug}`,
      "name": String(event.title || "")
    }))
  };

  if (loading) {
    return <div className="event-page-loading">Loading Events...</div>;
  }

  return (
    <>
      <Helmet>
        <title>Events | Rajput Shiksha Kosh</title>
        <meta
          name="description"
          content="View and manage upcoming and past events for Rajput Shiksha Kosh."
        />
        <script type="application/ld+json">
          {JSON.stringify(structuredSchema)}
        </script>
      </Helmet>

      <div className="event-page-wrapper">
        <div className="event-page-container">

          <div className="event-page-header">
            <h1 className="event-page-title">Upcoming & Past Events</h1>
            <div className="event-page-divider">
              <span className="event-page-line"></span>
              <GiFairyWings className="event-page-icon" />
              <span className="event-page-line"></span>
            </div>
          </div>

          <div className="event-page-filter-wrapper">
            <div className="event-page-filter-bar">
              <div className="event-page-filter-left">
                Explore Our Events
              </div>
              <div className="event-page-filter-right">
                <label>Filter By Year</label>
                <select value={activeYear} onChange={(e) => setActiveYear(e.target.value)}>
                  {years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="event-page-grid">
            {filteredEvents.length > 0 ? (
              filteredEvents.map((event) => (
                <Link to={`/events/${event.slug}`} key={event._id} className="event-page-card">
                  
                  <div className="event-page-card-top">
                    <div className="event-page-date-box">
                      <span className="event-page-month">{getMonthString(event.eventDate)}</span>
                      <span className="event-page-day">{getDayString(event.eventDate)}</span>
                    </div>
                  </div>

                  <div className="event-page-content">
                    <h2 className="event-page-card-title">{event.title}</h2>
                    
                    <div className="event-page-meta-list">
                      
                      <div className="event-page-meta">
                        <FaRegCalendarAlt className="event-page-meta-icon" />
                        <span>
                          {formatEventDateTime(event.eventDate, event.endDate, event.startTime, event.endTime)}
                        </span>
                      </div>
                      
                      <div className="event-page-meta">
                        <FaMapMarkerAlt className="event-page-meta-icon" />
                        <span>{event.eventPlace || "College Campus"}</span>
                      </div>

                    </div>
                  </div>
                  
                </Link>
              ))
            ) : (
              <div className="event-page-empty">No events found.</div>
            )}
          </div>

        </div>
      </div>
    </>
  );
}

export default Event;